'use client';

import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { useCallback, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { LinkIcon } from '@/components/tiptap-editor/tiptap-icons/link-icon';

// --- Lib ---
import { isMarkInSchema, isNodeTypeSelected, sanitizeUrl } from '@/lib/tiptap-utils';

/**
 * Configuration for the link popover functionality
 */
export interface UseLinkPopoverConfig {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor | null;
    /**
     * Whether to hide the link popover when not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
    /**
     * Callback function called when the link is set.
     */
    onSetLink?: () => void;
}

/**
 * Configuration for the link handler functionality
 */
export interface LinkHandlerProps {
    /**
     * The Tiptap editor instance.
     */
    editor: Editor | null;
    /**
     * Callback function called when the link is set.
     */
    onSetLink?: () => void;
}

/**
 * Checks if a link can be set in the current editor state
 */
export function canSetLink(editor: Editor | null): boolean {
    if (!editor || !editor.isEditable) return false;

    // The third argument 'true' checks whether the current selection is inside an image caption, and prevents setting a link there
    // If the selection is inside an image caption, we can't set a link
    if (isNodeTypeSelected(editor, ['image'], true)) return false;
    return editor.can().setMark('link');
}

/**
 * Checks if a link is currently active in the editor
 */
export function isLinkActive(editor: Editor | null): boolean {
    if (!editor || !editor.isEditable) return false;
    return editor.isActive('link');
}

/**
 * Determines if the link button should be shown
 */
export function shouldShowLinkButton(props: { editor: Editor | null; hideWhenUnavailable: boolean }): boolean {
    const { editor, hideWhenUnavailable } = props;

    const linkInSchema = isMarkInSchema('link', editor);

    if (!linkInSchema || !editor) {
        return false;
    }

    if (hideWhenUnavailable && !editor.isActive('code')) {
        return canSetLink(editor);
    }

    return true;
}

/**
 * Custom hook for handling link operations in a Tiptap editor
 */
type LinkHrefSnapshot = {
    href: string;
};

function linkHrefSnapshotEquality(a: LinkHrefSnapshot, b: LinkHrefSnapshot | null): boolean {
    return a.href === b?.href;
}

export function useLinkHandler(props: LinkHandlerProps) {
    const { editor, onSetLink } = props;
    const [url, setUrl] = useState('');
    const [prevHrefFromEditor, setPrevHrefFromEditor] = useState('');

    const linkHrefSelector = useCallback((ctx: EditorStateSnapshot<Editor | null>): LinkHrefSnapshot => {
        const ed = ctx.editor;
        if (!ed) return { href: '' };
        const { href } = ed.getAttributes('link');
        return { href: (typeof href === 'string' ? href : '') || '' };
    }, []);

    const linkHrefToolbar = useEditorState({
        editor,
        selector: linkHrefSelector,
        equalityFn: linkHrefSnapshotEquality,
    });

    const hrefFromEditor = linkHrefToolbar?.href ?? '';

    if (hrefFromEditor !== prevHrefFromEditor) {
        setPrevHrefFromEditor(hrefFromEditor);
        setUrl(hrefFromEditor);
    }

    const setLink = useCallback(() => {
        if (!url || !editor) return;

        const { selection } = editor.state;
        const isEmpty = selection.empty;

        let chain = editor.chain().focus();

        chain = chain.extendMarkRange('link').setLink({ href: url });

        if (isEmpty) {
            chain = chain.insertContent({ type: 'text', text: url });
        }

        chain.run();

        setUrl('');

        onSetLink?.();
    }, [editor, onSetLink, url]);

    const removeLink = useCallback(() => {
        if (!editor) return;
        editor.chain().focus().extendMarkRange('link').unsetLink().setMeta('preventAutolink', true).run();
        setUrl('');
    }, [editor]);

    const openLink = useCallback(
        (target: string = '_blank', features: string = 'noopener,noreferrer') => {
            if (!url) return;

            const safeUrl = sanitizeUrl(url, window.location.href);
            if (safeUrl !== '#') {
                window.open(safeUrl, target, features);
            }
        },
        [url],
    );

    return {
        url,
        setUrl,
        setLink,
        removeLink,
        openLink,
    };
}

type LinkStateSnapshot = {
    isVisible: boolean;
    canSet: boolean;
    isActive: boolean;
};

function linkStateSnapshotEquality(a: LinkStateSnapshot, b: LinkStateSnapshot | null): boolean {
    return a.isVisible === b?.isVisible && a.canSet === b?.canSet && a.isActive === b?.isActive;
}

/**
 * Custom hook for link popover state management
 */
export function useLinkState(props: { editor: Editor | null; hideWhenUnavailable: boolean }) {
    const { editor, hideWhenUnavailable = false } = props;

    const linkStateSelector = useCallback(
        (ctx: EditorStateSnapshot<Editor | null>): LinkStateSnapshot => {
            const ed = ctx.editor;
            if (!ed) {
                return { isVisible: false, canSet: false, isActive: false };
            }
            return {
                isVisible: shouldShowLinkButton({ editor: ed, hideWhenUnavailable }),
                canSet: canSetLink(ed),
                isActive: isLinkActive(ed),
            };
        },
        [hideWhenUnavailable],
    );

    const linkState = useEditorState({
        editor,
        selector: linkStateSelector,
        equalityFn: linkStateSnapshotEquality,
    });

    return {
        isVisible: linkState?.isVisible ?? false,
        canSet: linkState?.canSet ?? false,
        isActive: linkState?.isActive ?? false,
    };
}

/**
 * Main hook that provides link popover functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyLinkButton() {
 *   const { isVisible, canSet, isActive, Icon, label } = useLinkPopover()
 *
 *   if (!isVisible) return null
 *
 *   return <button disabled={!canSet}>Link</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedLinkButton() {
 *   const { isVisible, canSet, isActive, Icon, label } = useLinkPopover({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onSetLink: () => console.log('Link set!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       disabled={!canSet}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       <Icon />
 *       {label}
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useLinkPopover(config?: UseLinkPopoverConfig) {
    const { editor: providedEditor, hideWhenUnavailable = false, onSetLink } = config || {};

    const { editor } = useTiptapEditor(providedEditor);

    const { isVisible, canSet, isActive } = useLinkState({
        editor,
        hideWhenUnavailable,
    });

    const linkHandler = useLinkHandler({
        editor,
        onSetLink,
    });

    return {
        isVisible,
        canSet,
        isActive,
        label: 'Link',
        Icon: LinkIcon,
        ...linkHandler,
    };
}
